import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Card from './Card';
import Loader from './Loader';
import { usePageLoad } from '../hooks/usePageLoad';
import { useCardAnimation } from '../hooks/useCardAnimation';
import { formatEventForCard, formatDateParts } from '../utils/formatters';
import { createSlug } from '../utils/slugUtils';
import styles from '../styles/components/_EventListGSAP.module.scss';
import { useCustomNavigation } from '../components/NavigationContext';

gsap.registerPlugin(ScrollTrigger);

const EventListGSAP = ({ events = [], selectedEvent, eventSlug, isLoading, isPast = false }) => {
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const { customNavigate } = useCustomNavigation();
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );
  const { isAPILoaded, isPageLoaded } = usePageLoad(events);
  const { addToRefs, isTransitioning } = useCardAnimation(containerRef, true);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getCardOrientation = () => (windowWidth > 768 ? 'vertical' : 'horizontal');
  const getCardHeight = () => 'auto';

  const handleCardClick = (event) => {
    if (!event || !event.title) {
      console.warn("Données manquantes pour l'événement", event);
      return;
    }

    const eventTitle = typeof event.title === 'object' ? event.title.rendered : event.title;
    const slug = createSlug(eventTitle, event);

    if (!slug) {
      console.warn("Impossible de créer un slug valide pour l'événement:", eventTitle);
      return;
    }

    customNavigate(`/programmation/${slug}`, {
      state: { eventData: event },
      replace: true
    });
  };

  if (!isAPILoaded || !isPageLoaded) {
    return <Loader type="rings" isPageLoading={!isAPILoaded || !isPageLoaded} />;
  }

  return (
    <div className={styles.containerWrapper}>
      <div
        className={
          `${styles['event-list']} ${isTransitioning ? styles.transitioning : ''} ${isPast ? styles.past : ''}`
        }
        ref={containerRef}
      >
        {events.map((event, index) => {
          const formattedEvent = formatEventForCard(event);
          const dateParts = formatDateParts(formattedEvent.date, formattedEvent.dateFin);
          
          return (
            <div
              key={event.id || index}
              ref={addToRefs}
              className={styles['event-card-wrapper']}
            >
              <Card
                mainImage={formattedEvent.mainImage}
                title={formattedEvent.title}
                dateParts={dateParts}
                description={formattedEvent.description}
                orientation={getCardOrientation()}
                height={getCardHeight()}
                typePerformance={formattedEvent.typePerformance}
                subtitle={formattedEvent.subtitle}
                imagePosition="left"
                tags={formattedEvent.type ? [formattedEvent.type] : []}
                showOverlay={true}
                onClick={() => handleCardClick(event)}
                customNavigate={customNavigate}
                additionalArtists={formattedEvent.additionalArtists || []}
                addInfosEvent={formattedEvent.addInfosEvent}
                isPast={isPast}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EventListGSAP;